type Metafield = { value: string } | null;
type MediaRef = { image: { url: string } };
type FileRefUrl = { url: string };
type ReferenceNode = MediaRef | FileRefUrl;
type FileReference = { reference: ReferenceNode | null } | null;
type ProductNode = {
  handle: string;
  availableForSale: boolean;
  classic: Metafield;
  classicPattern: FileReference;
  isLimited: Metafield;
};
type ProductsPayload = { products: { nodes: ProductNode[] } };
type GraphQLResponse = {
  data?: ProductsPayload;
  errors?: Array<{ message?: string }>;
};

declare global {
  interface Window {
    STOREFRONT_TOKEN?: string;
  }
}

const selectElement = (
  selector: string,
  root: ParentNode = document,
): HTMLElement | null => root.querySelector<HTMLElement>(selector);

const buildQuery = (search: string) => ({
  query: `
    query ($first: Int!, $q: String!) {
      products(first: $first, query: $q) {
        nodes {
          handle
          availableForSale
          classic: metafield(namespace: "custom", key: "classic_color") { value }
          classicPattern: metafield(namespace: "custom", key: "classic_pattern_image") {
            reference {
              ... on MediaImage { image { url } }
              ... on GenericFile { url }
            }
          }
          isLimited: metafield(namespace: "custom", key: "is_limited_color") { value }
        }
      }
    }
  `,
  variables: { first: 100, q: search },
});

const fetchGroup = async (tag: string): Promise<ProductNode[]> => {
  const storefrontToken = window.STOREFRONT_TOKEN;
  if (!storefrontToken) {
    console.warn('[color-groups] Missing window.STOREFRONT_TOKEN — cannot query Storefront API');
    return [];
  }

  const queryString = `tag:${JSON.stringify(tag)}`;
  try {
    const response = await fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify(buildQuery(queryString)),
    });
    if (!response.ok) {
      console.warn(
        '[color-groups] Storefront fetch failed:',
        response.status,
        response.statusText,
        'query:',
        queryString,
      );
      return [];
    }
    let result: GraphQLResponse;
    try {
      result = await response.json();
    } catch (e) {
      console.warn('[color-groups] Failed to parse Storefront response JSON:', e);
      return [];
    }
    if (result.errors && result.errors.length > 0) {
      console.warn(
        '[color-groups] GraphQL errors:',
        result.errors.map((er) => er?.message || er),
      );
    }
    const nodes = result.data?.products.nodes ?? [];
    return nodes;
  } catch (e) {
    console.warn(
      '[color-groups] Network error during Storefront fetch:',
      e,
      'query:',
      queryString,
    );
    return [];
  }
};

const createSwatch = (
  handle: string,
  background: string,
  currentHandle: string,
  soldOut: boolean,
): HTMLAnchorElement => {
  const link = document.createElement('a');
  link.href = `/products/${handle}`;
  link.className = 'product-color-groups__swatch';
  if (soldOut) link.setAttribute('data-state', 'sold-out');
  const backgroundValue = background.trim();
  if (backgroundValue.includes('url(')) {
    const end = backgroundValue.indexOf(')');
    const urlPart =
      end > 0 ? backgroundValue.slice(0, end + 1) : backgroundValue;
    link.style.backgroundImage = urlPart;
  } else {
    link.style.backgroundColor = backgroundValue;
  }
  if (handle === currentHandle) link.setAttribute('aria-current', 'true');
  link.title = handle;
  link.setAttribute('aria-label', link.title);
  return link;
};

const appendSwatches = (
  row: Element | null,
  swatches: Array<{ handle: string; background: string; soldOut: boolean }>,
  currentHandle: string,
) => {
  if (!row) return;
  swatches.forEach((swatch) =>
    row.appendChild(
      createSwatch(swatch.handle, swatch.background, currentHandle, swatch.soldOut),
    ),
  );
};

const init = async () => {
  const container = selectElement('[data-color-groups]');
  if (!container) return;
  const debug = container.hasAttribute('data-debug');
  const groupTag = container.getAttribute('data-group-tag');
  const currentHandle = container.getAttribute('data-current-handle') || '';
  if (!groupTag) return;

  try {
    const products = await fetchGroup(groupTag);
    if (debug)
      console.log('[color-groups] tag:', groupTag, 'products:', products);
    if (products.length === 0) {
      if (debug) container.textContent = 'No color group products found';
      return;
    }

    const getRefUrl = (fileReference: FileReference): string => {
      const reference = fileReference?.reference;
      if (!reference) return '';
      return 'image' in reference && reference.image?.url
        ? reference.image.url
        : 'url' in reference && reference.url
          ? reference.url
          : '';
    };

    const limited: Array<{ handle: string; background: string; soldOut: boolean }> = [];
    const classic: Array<{ handle: string; background: string; soldOut: boolean }> = [];

    products.forEach((product) => {
      const url = getRefUrl(product.classicPattern);
      const color = product.classic?.value || '';
      const background = url ? `url("${url}")` : color;
      if (!background) return;
      const soldOut = !product.availableForSale;
      const isLimited =
        (product.isLimited?.value || '').toLowerCase() === 'true';
      if (isLimited) {
        limited.push({ handle: product.handle, background, soldOut });
      } else {
        classic.push({ handle: product.handle, background, soldOut });
      }
    });

    const limitedRow = container.querySelector('[data-row="limited"]');
    const classicRow = container.querySelector('[data-row="classic"]');
    const totalColors = limited.length + classic.length;
    if (totalColors === 0 && debug) {
      container.classList.remove('is-loading');
      container.textContent = 'No color metafields found';
      return;
    }

    if (totalColors === 1) {
      const wrapper = container.closest('.selector-wrapper');
      if (wrapper?.parentElement) wrapper.parentElement.removeChild(wrapper);
      else container.setAttribute('hidden', 'true');
      return;
    }

    const showLimited = limited.length > 0;
    const showClassic = classic.length > 0;

    const removeGroup = (type: 'limited' | 'classic') => {
      const group = container.querySelector(`[data-group-type="${type}"]`);
      group?.parentElement?.removeChild(group);
    };

    if (showLimited) appendSwatches(limitedRow, limited, currentHandle);
    else removeGroup('limited');

    if (showClassic) appendSwatches(classicRow, classic, currentHandle);
    else removeGroup('classic');

    container.classList.remove('is-loading');

    if (!showLimited && !showClassic) {
      const wrapper = container.closest('.selector-wrapper');
      if (wrapper?.parentElement) wrapper.parentElement.removeChild(wrapper);
      else container.setAttribute('hidden', 'true');
      return;
    }
  } catch (e) {
    if (debug) container.textContent = 'Failed to load color groups';
  }
};

document.addEventListener('DOMContentLoaded', init);

export {};
